import type { AgentView, MessageView } from "@walkie/protocol"
import { useMemo, useState } from "react"
import { agentColor, APP_SHORT } from "../lib/agents"
import { agentGone, orphanedBy } from "../lib/messages"
import { age } from "../lib/time"
import {
	claim,
	claimForMe,
	focusAgent,
	jumpToMessage,
	reassignRequest,
	release,
} from "../store/actions"
import { useFailures, useMyAgentIds, useOpenRequests } from "../store/derived"
import { setUi, useStore } from "../store/store"
import { HandleText, KindBadge, PersonSwatch, Popover, Ref, StateDot, Swatch, Tip } from "./ui"

type Group = "blockers" | "orphaned" | "mine" | "unclaimed" | "claimed"
const GROUPS: { id: Group; label: string }[] = [
	{ id: "blockers", label: "Blockers" },
	{ id: "orphaned", label: "Owner gone" },
	{ id: "mine", label: "Waiting on me" },
	{ id: "unclaimed", label: "Unclaimed" },
	{ id: "claimed", label: "Claimed" },
]

export function useRequestGroups() {
	const open = useOpenRequests()
	const mine = useMyAgentIds()
	const meId = useStore(s => s.me?.user.id)
	const agents = useStore(s => s.agents)
	const now = useStore(s => s.now)
	return useMemo(() => {
		const out: Record<Group, MessageView[]> = {
			blockers: [],
			orphaned: [],
			mine: [],
			unclaimed: [],
			claimed: [],
		}
		const isMine = (m: MessageView) => {
			const r = m.request!
			return (
				(!!meId &&
					(r.ownerUserId === meId ||
						r.claimedByUserId === meId ||
						m.toUsers.includes(meId))) ||
				(!!r.ownerAgentId && mine.has(r.ownerAgentId)) ||
				(!!r.claimedByAgentId && mine.has(r.claimedByAgentId)) ||
				m.toAgents.some(a => mine.has(a))
			)
		}
		for (const m of open) {
			if (m.kind === "blocker") out.blockers.push(m)
			else if (orphanedBy(m, agents, now)) out.orphaned.push(m)
			else if (isMine(m)) out.mine.push(m)
			else if (m.request!.status === "open") out.unclaimed.push(m)
			else out.claimed.push(m)
		}
		const sort = (a: MessageView, b: MessageView) =>
			Number(b.priority === "urgent") - Number(a.priority === "urgent") ||
			a.createdAt.localeCompare(b.createdAt)
		for (const g of Object.values(out)) g.sort(sort)
		return out
	}, [open, mine, meId, agents, now])
}

export function RequestsBoard() {
	const groups = useRequestGroups()
	const total = Object.values(groups).reduce((n, g) => n + g.length, 0)
	if (!total) return <div className="empty left">No open requests.</div>
	return (
		<div>
			{GROUPS.filter(g => groups[g.id].length).map(g => (
				<div key={g.id} role="group" aria-label={g.label}>
					<div className={"rgroup-h " + g.id}>
						{g.label} <span className="count">{groups[g.id].length}</span>
					</div>
					<div className="reqs">
						{groups[g.id].map(m => (
							<RequestItem key={m.id} m={m} />
						))}
					</div>
				</div>
			))}
		</div>
	)
}

function RequestItem({ m }: { m: MessageView }) {
	const r = m.request!
	const now = useStore(s => s.now)
	const selected = useStore(s => s.ui.selectedId === m.id)
	const canWrite = useStore(s => s.role !== "viewer")
	const author = useStore(s =>
		m.author.type === "agent" && m.author.id ? s.agents[m.author.id] : undefined,
	)
	const agents = useStore(s => s.agents)
	const gone = orphanedBy(m, agents, now)
	const [reassign, setReassign] = useState<HTMLElement | null>(null)
	const messages = useStore(s => s.messages)
	/** A handoff reply that cites this request: how a reassignment is recorded. */
	const handedTo = useMemo(() => {
		if (!gone) return null
		const root = m.threadId ?? m.id
		return (
			Object.values(messages)
				.filter(x => x.threadId === root && x.kind === "handoff" && x.cites.includes(m.id))
				.sort((a, b) => b.seq - a.seq)[0] ?? null
		)
	}, [gone, messages, m.id, m.threadId])
	const label = m.title ?? m.body.split("\n")[0]!.slice(0, 140)
	return (
		<div
			className={
				"req" +
				(m.priority === "urgent" ? " urgent" : "") +
				(selected ? " sel" : "") +
				(gone ? " orphan" : "")
			}
		>
			<div className="req-l1">
				{m.priority === "urgent" && <span className="kind k-urgent">urgent</span>}
				<KindBadge kind={m.kind} />
				<button className="req-title ell" onClick={() => jumpToMessage(m)} title={m.body}>
					{label}
				</button>
				<Tip content={`opened ${new Date(m.createdAt).toLocaleString()}`}>
					<span className="mono dim">{age(m.createdAt, now)}</span>
				</Tip>
			</div>
			<div className="req-l2 main">
				{author ? (
					<button
						className="who"
						style={{ color: agentColor(author.color), fontSize: 11 }}
						onClick={() => focusAgent(author.id)}
					>
						@{author.handle}
					</button>
				) : (
					<span style={{ fontWeight: 600 }}>{m.author.handle}</span>
				)}
				{(r.ownerAgentId || r.ownerUserId) && (
					<>
						<span>→</span>
						<HandleText id={r.ownerAgentId ?? r.ownerUserId} />
					</>
				)}
				{r.status === "claimed" ? (
					<span className="status claimed">
						◐ <HandleText id={r.claimedByAgentId ?? r.claimedByUserId} />
						{r.eta && <span className="dim"> eta {r.eta}</span>}
						{r.claimedAt && <span className="dim mono"> {age(r.claimedAt, now)}</span>}
					</span>
				) : (
					<span className="status open">● unclaimed</span>
				)}
				{canWrite && !gone && (
					<span className="req-actions">
						{r.status === "open" ? (
							<button
								className="btn btn-xs"
								onClick={() => claim(m.id)}
								aria-label={`Claim: ${label}`}
							>
								Claim
							</button>
						) : (
							<button
								className="btn btn-xs btn-ghost"
								onClick={() => release(m.id)}
								aria-label={`Release: ${label}`}
							>
								Release
							</button>
						)}
						<button
							className="btn btn-xs"
							onClick={() => setUi({ resolveFor: m.id, selectedId: m.id })}
							aria-label={`Resolve: ${label}`}
						>
							Resolve
						</button>
					</span>
				)}
			</div>
			{gone && (
				<div className="req-l2 orphan-row">
					<Tip
						content={`@${gone.handle} ${gone.state === "ended" ? "has ended" : `has been offline for ${age(gone.lastSeenAt, now)}`}; nobody will pick this up. Claim it yourself or reassign it.`}
					>
						<span
							className="gone-badge"
							tabIndex={0}
							aria-label={`Owner gone: @${gone.handle} ${gone.state === "ended" ? "has ended" : `has been offline for ${age(gone.lastSeenAt, now)}`}`}
						>
							owner gone
						</span>
					</Tip>
					{handedTo ? (
						<button
							className="link ell"
							onClick={() => jumpToMessage(handedTo)}
							title="Open the handoff reply"
						>
							↪ reassigned to{" "}
							<HandleText
								id={handedTo.request?.ownerAgentId ?? handedTo.request?.ownerUserId}
								handle={handedTo.toAgents.length ? undefined : "someone"}
							/>
						</button>
					) : null}
					{canWrite && (
						<span className="req-actions">
							<button
								className="btn btn-xs"
								onClick={() => void claimForMe(m.id)}
								aria-label={`Claim for me: ${label}`}
							>
								Claim for me
							</button>
							<button
								className="btn btn-xs"
								aria-haspopup="menu"
								aria-expanded={!!reassign}
								onClick={e => setReassign(reassign ? null : e.currentTarget)}
								aria-label={`Reassign: ${label}`}
							>
								Reassign…
							</button>
							<button
								className="btn btn-xs btn-ghost"
								onClick={() => setUi({ resolveFor: m.id, selectedId: m.id })}
								aria-label={`Resolve: ${label}`}
							>
								Resolve
							</button>
						</span>
					)}
					{reassign && (
						<ReassignMenu
							anchor={reassign}
							exclude={gone.id}
							onClose={() => setReassign(null)}
							onPick={h => {
								setReassign(null)
								void reassignRequest(m.id, h, gone.handle)
							}}
						/>
					)}
				</div>
			)}
			{(r.deps.length > 0 || r.evidence.length > 0 || r.claimNote) && (
				<div className="req-l2">
					{r.deps.map(d => (
						<span
							key={d.kind + d.target}
							className={"dep" + (d.satisfiedAt ? " ok" : "")}
							title={`${d.kind.replace("_", " ")}${d.satisfiedAt ? " — satisfied" : " — waiting"}`}
						>
							{d.satisfiedAt ? "✓" : "◌"} {d.target}
						</span>
					))}
					{r.evidence.map((e, i) => (
						<span key={i} className="evidence">
							<Ref value={e.ref} />
							{e.verified && <span className="v">✓</span>}
						</span>
					))}
					{r.claimNote && <span className="dim ell">“{r.claimNote}”</span>}
				</div>
			)}
		</div>
	)
}

/** Live agents (participating) and people a request can be handed to. */
function ReassignMenu(props: {
	anchor: HTMLElement
	exclude: string
	onClose: () => void
	onPick: (handle: string) => void
}) {
	const agents = useStore(s => s.agents)
	const people = useStore(s => s.people)
	const now = useStore(s => s.now)
	const [q, setQ] = useState("")
	const rank = { active: 0, idle: 1, offline: 2, ended: 3 }
	const live: AgentView[] = Object.values(agents)
		.filter(
			a =>
				a.id !== props.exclude &&
				a.shared &&
				!agentGone(a, now) &&
				a.handle.includes(q.toLowerCase().replace(/^@/, "")),
		)
		.sort((a, b) => rank[a.state] - rank[b.state] || a.handle.localeCompare(b.handle))
		.slice(0, 10)
	const ps = people.filter(p => p.handle.includes(q.toLowerCase().replace(/^@/, "")))
	return (
		<Popover anchor={props.anchor} onClose={props.onClose} label="Reassign to" align="right">
			<div className="menu-label">Reassign to</div>
			<input
				className="input picker-input"
				value={q}
				placeholder="@handle"
				aria-label="Filter handles"
				onChange={e => setQ(e.target.value)}
				onKeyDown={e => {
					const first = live[0]?.handle ?? ps[0]?.handle
					if (e.key === "Enter" && first) {
						e.preventDefault()
						props.onPick(first)
					}
				}}
			/>
			{live.map(a => (
				<button
					key={a.id}
					className="menu-item"
					role="menuitem"
					onClick={() => props.onPick(a.handle)}
				>
					<Swatch agent={a} />
					<b style={{ color: agentColor(a.color) }}>@{a.handle}</b>
					<span className="dim ell grow">
						{a.ownerHandle} · {APP_SHORT[a.app]}
						{a.lastSeenAt && a.state !== "active" ? ` · ${age(a.lastSeenAt, now)}` : ""}
					</span>
					<StateDot state={a.state} />
				</button>
			))}
			{ps.length > 0 && <div className="menu-sep" />}
			{ps.map(p => (
				<button
					key={p.userId}
					className="menu-item"
					role="menuitem"
					onClick={() => props.onPick(p.handle)}
				>
					<PersonSwatch handle={p.handle} />
					<b>@{p.handle}</b>
					<span className="dim ell grow">{p.name}</span>
				</button>
			))}
		</Popover>
	)
}

export function FailuresList() {
	const failures = useFailures()
	const agents = useStore(s => s.agents)
	const now = useStore(s => s.now)
	if (!failures.length) return <div className="empty left">All deliveries on track.</div>
	return (
		<div role="list" aria-label="Delivery failures">
			{failures.slice(0, 50).map(({ message: m, delivery: d }) => {
				const a = agents[d.agentId]
				return (
					<div key={m.id + d.agentId} className="fail" role="listitem">
						<span className="dg failed" aria-hidden>
							✕
						</span>
						<div style={{ minWidth: 0 }} className="row">
							<button
								className="who"
								style={{ color: a ? agentColor(a.color) : undefined, fontSize: 11 }}
								onClick={() => a && focusAgent(a.id)}
							>
								@{a?.handle ?? d.agentId.slice(0, 8)}
							</button>
							<button className="fail-msg ell" onClick={() => jumpToMessage(m)}>
								{m.title ?? m.body.split("\n")[0]}
							</button>
						</div>
						<Tip
							content={
								d.lastError
									? `${d.lastError} · ${d.attempts} attempts`
									: `${d.attempts} attempts; ${a?.state === "offline" ? "agent is offline" : "connector has not acknowledged"}`
							}
						>
							<span className="mono dim" tabIndex={0}>
								{age(d.acceptedAt, now)}
								{d.attempts > 1 ? ` ×${d.attempts}` : ""}
							</span>
						</Tip>
					</div>
				)
			})}
		</div>
	)
}
